/**
 * Event Utility Functions for YearlyCalendar Component
 * Handles event processing, normalization, sorting, and rendering calculations
 */
import { CalendarEvent, ProcessedEventMap } from '../../../contracts/YearlyCalendarContracts';
import { getDateKey, normalizeDate, getDaysBetween, getDay, getInclusiveEndDate, differenceInCalendarDays } from './dateUtils';

/**
 * Normalize event dates to timestamps for comparison
 */
export const normalizeEventDates = (event: CalendarEvent): CalendarEvent => {
  return {
    ...event,
    _startDt: normalizeDate(event.startDt).getTime(),
    // Use the inclusive end day so all-day / holiday events whose end sits on the
    // next day's midnight are not counted an extra day.
    _endDt: normalizeDate(getInclusiveEndDate(event.endDt)).getTime(),
  };
};

/**
 * Sort events by start date, then by end date
 * Events with earlier start dates come first
 * For same start date, shorter events come first
 */
export const sortEvents = (events: CalendarEvent[]): CalendarEvent[] => {
  return [...events].sort((a, b) => {
    const startA = a._startDt ?? normalizeDate(a.startDt).getTime();
    const startB = b._startDt ?? normalizeDate(b.startDt).getTime();
    
    if (startA === startB) {
      const endA = a._endDt ?? normalizeDate(a.endDt).getTime();
      const endB = b._endDt ?? normalizeDate(b.endDt).getTime();
      return endA - endB;
    }
    
    return startA - startB;
  });
};

/**
 * Get the next available order slot for an event on a given day
 * Order 1 = top position, Order 2 = bottom position
 */
export const getNextOrder = (existingEvents: CalendarEvent[], maxVisible: number = 2): number => {
  if (existingEvents.length === 0) return 1;
  
  const usedOrders = new Set(existingEvents.map(e => e.order).filter(Boolean));
  
  for (let order = 1; order <= maxVisible + 1; order++) {
    if (!usedOrders.has(order)) return order;
  }
  
  return existingEvents.length + 1;
};

/**
 * Calculate paint box length (how many days the event bar should span)
 * Respects week boundaries (resets at week start)
 */
export const calculatePaintLength = (
  currentDay: Date,
  eventEndDate: Date | number,
  startingDay: number = 0
): number => {
  const dayOfWeek = getDay(currentDay);
  const adjustedDayOfWeek = (dayOfWeek - startingDay + 7) % 7;
  const daysUntilWeekEnd = 7 - adjustedDayOfWeek;
  
  const endTime = typeof eventEndDate === 'number' ? eventEndDate : normalizeDate(eventEndDate).getTime();
  // Count whole calendar days (inclusive) rather than dividing an elapsed
  // millisecond span by 24h. A fixed-24h divisor over-counts by a day across a
  // DST fall-back (e.g. a 49-hour span would otherwise report 4 days for 3).
  const remainingDays = differenceInCalendarDays(new Date(endTime), normalizeDate(currentDay)) + 1;

  return Math.min(daysUntilWeekEnd, remainingDays);
};

/**
 * Check if this is a week's first day (for determining when to start painting)
 */
export const isWeekFirstDay = (date: Date, startingDay: number = 0): boolean => {
  return getDay(date) === startingDay;
};

/**
 * Process events for a calendar view
 * Distributes events across days and calculates rendering properties
 */
export const processEventsForView = (
  events: CalendarEvent[],
  visibleDays: Date[],
  startingDay: number = 0,
  expandedMode: boolean = false
): ProcessedEventMap => {
  const eventMap: ProcessedEventMap = {};
  
  // Initialize all visible days in the map
  visibleDays.forEach(day => {
    eventMap[getDateKey(day)] = [];
  });
  
  // Create a set of visible day timestamps for quick lookup
  const visibleDaySet = new Set(visibleDays.map(d => normalizeDate(d).getTime()));
  
  // Normalize and sort events
  const normalizedEvents = events.map(normalizeEventDates);
  const sortedEvents = sortEvents(normalizedEvents);
  
  // Process each event
  sortedEvents.forEach(event => {
    // Use the inclusive end so all-day / holiday events ending at the next day's
    // midnight do not paint an extra trailing day.
    const eventDays = getDaysBetween(event.startDt, getInclusiveEndDate(event.endDt));
    
    let isFirstDayOfEvent = true;
    
    eventDays.forEach((day, index) => {
      const dayTimestamp = normalizeDate(day).getTime();
      
      // Skip if day is not visible
      if (!visibleDaySet.has(dayTimestamp)) {
        return;
      }
      
      const dayKey = getDateKey(day);
      const existingEvents = eventMap[dayKey] || [];
      
      // Determine if we should start painting a new bar
      const isWeekStart = isWeekFirstDay(day, startingDay);
      const shouldStartPaint = isFirstDayOfEvent || isWeekStart;
      
      // Calculate paint box length
      const paintLength = shouldStartPaint
        ? calculatePaintLength(day, event._endDt!, startingDay)
        : 0;
      
      // Get order for this event
      const order = getNextOrder(existingEvents, expandedMode ? Infinity : 2);
      
      // Clone event with day-specific properties
      const eventForDay: CalendarEvent = {
        ...event,
        order,
        startPaint: shouldStartPaint,
        paintBoxLength: paintLength,
      };
      
      eventMap[dayKey] = [...existingEvents, eventForDay];
      isFirstDayOfEvent = false;
    });
  });
  
  return eventMap;
};

/**
 * Get events for a specific day
 */
export const getEventsForDay = (eventMap: ProcessedEventMap, date: Date): CalendarEvent[] => {
  return eventMap[getDateKey(date)] || [];
};

/**
 * Count events for a specific day
 */
export const getEventCountForDay = (eventMap: ProcessedEventMap, date: Date): number => {
  return getEventsForDay(eventMap, date).length;
};

/**
 * Check if a day has more events than visible
 */
export const hasMoreEvents = (
  eventMap: ProcessedEventMap,
  date: Date,
  maxVisible: number = 2
): boolean => {
  return getEventCountForDay(eventMap, date) > maxVisible;
};

/**
 * Get visible events for a day (respecting max visible limit)
 */
export const getVisibleEvents = (
  eventMap: ProcessedEventMap,
  date: Date,
  maxVisible: number = 2,
  expandedMode: boolean = false
): CalendarEvent[] => {
  const events = getEventsForDay(eventMap, date);
  
  if (expandedMode) return events;
  
  return events.filter(e => (e.order ?? 0) <= maxVisible);
};

/**
 * Check if an event is clickable
 */
export const isEventClickable = (event: CalendarEvent): boolean => {
  if (event.isClickable === false) return false;
  if (event.isFreeFlexHours) return false;
  return true;
};

/**
 * Get event background color with fallback
 */
export const getEventColor = (event: CalendarEvent, defaultColor: string = '#0078d4'): string => {
  return event.bgcolor || defaultColor;
};

/**
 * Get event text color (auto-calculated for contrast if not provided)
 */
export const getEventTextColor = (event: CalendarEvent, defaultColor: string = '#ffffff'): string => {
  return event.color || defaultColor;
};
